/** On-demand Vehicle Pre-Trip Inspection PDF (CONF-20260724-inspection-pdf-history). */

import { jsPDF } from 'jspdf';
import { checklistItemLabel, CCI_BODY_VIEWS } from './cciCatalog';
import { expandAfterChecklistItems } from './afterChecklist';
import {
  FITNESS_QUESTION_IDS,
  parseFitnessAnswers,
  type FitnessQuestionId,
} from './fitnessCatalog';
import type { InspectionBodyDefect, VehicleInspection } from '../types/database';

const FITNESS_LABELS: Record<FitnessQuestionId, string> = {
  fatigue_free: 'I am not impaired by fatigue that would prevent me from driving safely.',
  medication_clear: 'I am not affected by medication that would impair my ability to drive.',
  alcohol_drugs_clear: 'I am not affected by alcohol or drugs.',
  rest_breaks_ok: 'I have completed the minimum rest required by law in the past 24 and 72 hours.',
  seven_hours_rest: 'I have had at least seven consecutive hours of stationary rest since my last shift.',
  driving_hours_ok:
    'Under my fatigue management system (Standard / BFM / AFM), I have enough driving hours left to complete this duty (including a reasonable delay buffer).',
  other_factors_clear: 'There are no other factors that would prevent me from safely completing this duty.',
};

export type InspectionPdfSource = VehicleInspection & {
  drivers?: { name?: string | null } | null;
  vehicles?: {
    fleet_number?: string | null;
    model?: string | null;
    registration_plate?: string | null;
  } | null;
};

function vehicleLabel(row: InspectionPdfSource): string {
  const v = row.vehicles;
  if (v) {
    const plate = v.registration_plate?.trim();
    const fleet = v.fleet_number?.trim();
    if (plate && fleet) return `${plate} (${fleet})`;
    if (plate || fleet) return plate || fleet || '—';
    if (v.model?.trim()) return v.model.trim();
  }
  return row.work_fields?.rego?.trim() || '—';
}

function bodyViewLabel(view: string): string {
  return CCI_BODY_VIEWS.find((b) => b.id === view)?.label ?? view;
}

function statusLabel(status: string): string {
  if (status === 'pass') return 'Pass';
  if (status === 'fail') return 'Fail';
  if (status === 'na') return 'N/A';
  if (status === 'unanswered') return 'Unanswered';
  return status || '—';
}

/** Trigger a browser download for a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function inspectionPdfFilename(row: InspectionPdfSource): string {
  const day = row.inspection_date || 'unknown-date';
  const idShort = row.id?.slice(0, 8) || 'report';
  return `inspection-${day}-${idShort}.pdf`;
}

/**
 * Build an English PDF report from a vehicle inspection row.
 * Call only on user download action (do not generate on submit).
 */
export async function buildInspectionPdfBlob(row: InspectionPdfSource): Promise<Blob> {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeLine = (text: string, opts?: { bold?: boolean; size?: number; color?: [number, number, number] }) => {
    const size = opts?.size ?? 10;
    doc.setFontSize(size);
    doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
    if (opts?.color) doc.setTextColor(...opts.color);
    else doc.setTextColor(20, 20, 20);
    const lines = doc.splitTextToSize(text, maxWidth) as string[];
    for (const line of lines) {
      ensureSpace(size + 4);
      doc.text(line, margin, y);
      y += size + 4;
    }
  };

  const section = (title: string) => {
    y += 8;
    ensureSpace(20);
    writeLine(title, { bold: true, size: 12 });
    y += 2;
  };

  writeLine('Vehicle Pre-Trip Inspection Report', { bold: true, size: 16 });
  writeLine(`Generated: ${new Date().toISOString()}`, { size: 9, color: [100, 100, 100] });
  y += 6;

  section('Summary');
  const driverName =
    row.drivers?.name?.trim() || row.work_fields?.driver_name?.trim() || '—';
  writeLine(`Inspection ID: ${row.id}`);
  writeLine(`Date: ${row.inspection_date || '—'}`);
  writeLine(`Vehicle: ${vehicleLabel(row)}`);
  writeLine(`Driver: ${driverName}`);
  writeLine(`Type: ${row.inspection_type || '—'}`);
  writeLine(`Severity: ${row.severity_result || '—'}`);
  writeLine(`Declared at: ${row.declaration_accepted_at || '—'}`);
  writeLine(`Declaration version: ${row.declaration_text_ver || '—'}`);
  writeLine(`AFTER completed at: ${row.after_completed_at || '—'}`);

  const wf = row.work_fields ?? {};
  if (wf.tour_name || wf.start_km || wf.fuel_level || wf.finish_km || wf.depot_finish_time) {
    section('Work fields');
    if (wf.tour_name) writeLine(`Tour: ${wf.tour_name}`);
    if (wf.pick_up) writeLine(`Pick up: ${wf.pick_up}`);
    if (wf.drop_off) writeLine(`Drop off: ${wf.drop_off}`);
    if (wf.start_km) writeLine(`Start KM: ${wf.start_km}`);
    if (wf.finish_km) writeLine(`Finish KM: ${wf.finish_km}`);
    if (wf.depot_start_time) writeLine(`Depot start: ${wf.depot_start_time}`);
    if (wf.depot_finish_time) writeLine(`Depot finish: ${wf.depot_finish_time}`);
    if (wf.fuel_level) writeLine(`Fuel: ${wf.fuel_level}`);
  }

  section('BEFORE checklist');
  const items = row.items ?? [];
  if (items.length === 0) {
    writeLine('(No checklist items)');
  } else {
    for (const item of items) {
      const note = item.note?.trim() ? ` — ${item.note.trim()}` : '';
      writeLine(`${checklistItemLabel(item.id)}: ${statusLabel(item.status)}${note}`);
    }
  }

  section('AFTER checklist (end of shift)');
  const afterItems = expandAfterChecklistItems(row.after_items);
  const afterStarted = (row.after_items?.length ?? 0) > 0 || !!row.after_completed_at;
  if (!afterStarted) {
    writeLine('(Not completed)');
  } else {
    for (const item of afterItems) {
      const note = item.note?.trim() ? ` — ${item.note.trim()}` : '';
      writeLine(`${checklistItemLabel(item.id)}: ${statusLabel(item.status)}${note}`);
    }
  }

  section('Body defects');
  const defects = (row.body_defects ?? []) as InspectionBodyDefect[];
  if (defects.length === 0) {
    writeLine('(None)');
  } else {
    for (const d of defects) {
      writeLine(`${bodyViewLabel(d.view)}: ${d.note?.trim() || '—'}`);
    }
  }

  section('Fitness to Drive');
  const fitness = parseFitnessAnswers(row.work_fields?.fitness);
  if (!fitness) {
    writeLine('(No fitness declaration)');
  } else {
    writeLine(`Form version: ${fitness.form_ver}`);
    writeLine(`Signed name: ${fitness.signed_name || '—'}`);
    writeLine(`Signed at: ${fitness.signed_at || '—'}`);
    for (const qid of FITNESS_QUESTION_IDS) {
      const v = fitness.answers[qid];
      const ans = v === true ? 'Yes' : v === false ? 'No' : '—';
      writeLine(`${FITNESS_LABELS[qid]}: ${ans}`);
    }
  }

  if (row.notes?.trim()) {
    section('Notes');
    writeLine(row.notes.trim());
  }

  y += 12;
  writeLine('— End of report —', { size: 9, color: [120, 120, 120] });

  return doc.output('blob');
}

export async function downloadInspectionPdf(row: InspectionPdfSource): Promise<void> {
  const blob = await buildInspectionPdfBlob(row);
  downloadBlob(blob, inspectionPdfFilename(row));
}
